import { api } from './client'

export interface MeResponse {
  user_id: number
  username: string
  email: string | null
  roles: string[]
  is_superuser: boolean
  has_demo_access: boolean
  /** Si ya tiene PIN de 6 dígitos para entrar por kit. Sin PIN, `PlayBridge`
   *  ofrece crearlo (`pinCreate`) antes de abrir el juego. */
  has_pin?: boolean
  /**
   * Campos del player_profile requeridos por el onboarding que todavía
   * están vacíos. Ya NO bloquean el juego (mg 2026-09-07): quedan como CTA
   * "Completar perfil" en `/data`. Hoy:
   * `["age", "gender", "position", "vr_experience"]`.
   */
  pending_profile_fields: string[]
  /**
   * Invitaciones a cuestionarios pendientes (status pending) para el email
   * del usuario. El gate BLOQUEANTE vive adentro del vr-app (después de
   * "Comenzar"); en el web-app queda como recordatorio en `/data`.
   * `name_es` sale de QUESTIONNAIRES[code].
   */
  pending_questionnaires: { code: string; timepoint: string | null; name_es: string }[]
  /**
   * Filas de ClubStaff del usuario, joineadas a Club para el nombre. Vacío
   * para jugadores/admins sin club. Enruta los paneles Manager/Club.
   */
  club_memberships: { club_id: number; club_name: string; club_role: string }[]
  /**
   * Gate de consentimiento digital (self-service, ver `know.recordMyConsent`
   * → `POST /know/me/consent`). `true` si el usuario es staff de algún club
   * (eximido) o si ya tiene un Consent vigente. Lo usan `RequireConsent` /
   * `/consent` (antes de un cuestionario en la web) y el vr-app (antes de
   * la experiencia).
   */
  consent_ok: boolean
  /**
   * Gate de T&C bloqueante para STAFF de club (self-service, ver
   * `club.recordMyTerms` → `POST /club/me/terms`). Distinto de `consent_ok`
   * (piloto científico, jugador): `true` si el usuario NO es staff de ningún
   * club (un jugador puro nunca ve este gate) o si ya aceptó los términos.
   * ClubPage (y la ruta `/club/players/:userId`) lo usan para un gate
   * BLOQUEANTE antes de mostrar el panel.
   */
  terms_ok: boolean
  /**
   * "Sin club no se hace nada" (mg, 2026-09-09). `true` cuando el usuario NO
   * tiene perfil de jugador con club: no puede abrir sesiones (el backend
   * responde 403 `club_required` en `POST /sessions`). Es `true` también para
   * un manager sin perfil de jugador y para un superadmin — ninguno de los dos
   * JUEGA hasta que un club lo dé de alta.
   *
   * No lo uses solo para decidir la pantalla de bloqueo: eso es
   * `isClubless()` en `auth/postLoginRoute.ts`, que además exige que no sea
   * staff ni superadmin (a esos les quedan `/club` y `/admin`).
   */
  club_required: boolean
  /**
   * Branding del club del JUGADOR (via player_profile.club_id, distinto de
   * club_memberships que es de STAFF). Null si no tiene club asignado. Lo usa
   * AppNavBar para pintar el acento / logo del club sobre el header.
   */
  player_club: {
    id: number
    name: string
    logo_url: string | null
    primary_color: string | null
  } | null
  /**
   * Branding del club del usuario logueado si es staff (via club_memberships[0]).
   * Contiene primary_color, secondary_color, accent_color para temar el header.
   * Null si no es staff de ningún club.
   */
  branding_club: {
    id: number
    name: string
    logo_url: string | null
    primary_color: string | null
    secondary_color: string | null
    accent_color: string | null
  } | null
  /**
   * Si el usuario logeado es una impersonación de admin, contiene los datos
   * del admin impersonante. Null si es una sesión normal.
   */
  impersonating: {
    impersonator_user_id: number
    impersonator_username: string
  } | null
  profile: {
    display_name: string
    handedness: string | null
    dominant_foot: string | null
    height_cm: number | null
  } | null
}

export interface LoginPayload {
  email?: string
  username?: string
  password: string
}

export interface LoginResponse {
  token: string
  user_id: number
  username: string
  roles: string[]
  is_superuser: boolean
  has_demo_access: boolean
  pending_profile_fields: string[]
}

export const auth = {
  /** Logs in via JSON `POST /auth/login` AND seeds the cookie session via
   *  `POST /auth/login-form` so cookie + bearer stay aligned. The cookie is
   *  what the SPA actually reads (cookie session), but we keep the token
   *  exchange surface alive for the vr-app handoff. */
  async login(identifier: string, password: string): Promise<LoginResponse> {
    const body: LoginPayload = identifier.includes('@')
      ? { email: identifier, password }
      : { username: identifier, password }
    const jsonRes = await api.post<LoginResponse>('/auth/login', body)
    // Cookie session — same credentials, form-encoded surface.
    const form = new FormData()
    form.set('identifier', identifier)
    form.set('password', password)
    form.set('next', '/')
    await fetch(`${import.meta.env.VITE_API_BASE ?? '/api'}/auth/login-form`, {
      method: 'POST',
      credentials: 'include',
      body: form,
      redirect: 'manual',
    })
    return jsonRes
  },

  async signup(email: string, password: string, displayName?: string): Promise<LoginResponse> {
    const r = await api.post<LoginResponse>('/auth/signup', {
      email,
      password,
      display_name: displayName,
    })
    // Mirror the same auth into the cookie session.
    const form = new FormData()
    form.set('identifier', email)
    form.set('password', password)
    form.set('next', '/')
    await fetch(`${import.meta.env.VITE_API_BASE ?? '/api'}/auth/login-form`, {
      method: 'POST',
      credentials: 'include',
      body: form,
      redirect: 'manual',
    })
    return r
  },

  me: () => api.get<MeResponse>('/auth/me'),

  async logout(): Promise<void> {
    const form = new FormData()
    await fetch(`${import.meta.env.VITE_API_BASE ?? '/api'}/logout`, {
      method: 'POST',
      credentials: 'include',
      body: form,
      redirect: 'manual',
    })
  },

  stopImpersonation: () => api.post<{ ok: boolean }>('/auth/impersonate/stop', {}),

  /** Setea el PIN de 6 dígitos para entrar desde el casco. `token` viene del
   *  link mandado por mail. 400/410 = token inválido/vencido, 422 = PIN
   *  inválido (no son 6 dígitos numéricos exactos) — el caller distingue por
   *  `ApiError.status`. */
  pinSetup: (token: string, pin: string) =>
    api.post<{ ok: boolean; logged_in?: boolean; club_name?: string; club_code?: string }>(
      '/auth/pin/setup',
      { token, pin },
    ),

  /** El jugador logueado sin PIN crea el suyo. 409 `pin_exists` si ya tenía
   *  (cambiarlo va por el link del mail), 409 `pin_taken` si otro jugador del
   *  club ya lo usa. */
  pinCreate: (pin: string) => api.post<{ ok: boolean }>('/auth/pin', { pin }),

  /** Pide un mail nuevo con link de setup de PIN. Siempre 200 (no filtra si
   *  el email existe). */
  pinResetRequest: (email: string) =>
    api.post<{ ok: boolean }>('/auth/pin/reset-request', { email }),

  /** Cookie → TICKET EFÍMERO para el handoff al vr-app.
   *
   *  Desde 2026-09-08 (vrs-docs/52 H-6 / D14) esto ya NO devuelve el token
   *  permanente del usuario: devuelve un ticket de un solo uso que vive
   *  `expires_in` segundos (60). Quien lo canjea por el Bearer real es el
   *  vr-app, con `POST /auth/redeem-ticket` — nunca por URL. Ver
   *  `auth/vrBridge.ts` para cómo viaja el ticket. */
  exchangeToken: () =>
    api.post<{
      ticket: string
      expires_in: number
      user_id: number
      username: string
    }>('/auth/exchange-token'),

  googleLoginUrl(next: string = '/'): string {
    const base = import.meta.env.VITE_API_BASE ?? '/api'
    return `${base}/auth/google/login?next=${encodeURIComponent(next)}`
  },
}
